import { tagFailure } from '@forest/paywall-core'
import { useStore } from '@nanostores/react'
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { contentTitleKey } from '~/lib/content-title-key'
import { getItemTypes } from '~/services/api/delivery-service'
import {
  getActivity,
  getClaimableRewards,
} from '~/services/api/gamepass-service'
import { $config } from '~/store/config'
import { $userInfo } from '~/store/user-info'
import type { ItemContent, ItemType } from '~/types/delivery'
import type { ActivityProgressionStep } from '~/types/game-pass'

export function getActivityId(): number {
  return $config.get()!.activity_id
}

/** The activity query's cache key; the id is undefined until config loads. */
export function activityQueryKey(activityId: number | undefined) {
  return ['activity', activityId] as const
}

/**
 * Reads the accumulated score straight off the cached activity query — the
 * one place that knows the raw cache shape and key. Null when the activity
 * isn't cached or has no recorded progress yet.
 */
export function getCachedActivityScore(
  queryClient: QueryClient,
): number | null {
  const cached = queryClient.getQueryData<ActivityQueryData>(
    activityQueryKey(getActivityId()),
  )
  return cached?.activity.progress?.accumulated_score ?? null
}

export type RewardStatus = 'claimable' | 'pending' | 'claimed'

export interface Reward {
  stepId: number
  iconUrl: string
  type: ItemContent['type']
  amount: number
  /** Names this content in the web string catalog; null when unnamed. */
  titleKey: string | null
  status: RewardStatus
  requiredScore: number
  unlockCondition: ActivityProgressionStep['unlock_condition']
  claimableCreatedAt: string | null
}

async function fetchActivityData(activityId: number, userId: number) {
  const [activity, claimableRewards] = await Promise.all([
    getActivity(activityId, userId),
    getClaimableRewards(activityId),
  ])

  const itemTypeIds = [
    ...new Set(activity.progression_steps.map((s) => s.reward_item_type_id)),
  ]
  const { item_types } = await getItemTypes(itemTypeIds)

  const claimableStepIds = new Set(
    claimableRewards.rewards
      .filter((r) => r.status === 'delivered')
      .map((r) => r.progression_step_id),
  )

  const claimableCreatedAtMap = new Map(
    claimableRewards.rewards.map((r) => [r.progression_step_id, r.created_at]),
  )

  return {
    activity,
    itemTypes: item_types,
    claimableStepIds,
    claimableCreatedAtMap,
  }
}

/** What the activity query caches — `select` below derives the view model. */
type ActivityQueryData = Awaited<ReturnType<typeof fetchActivityData>>

export function useActivityQuery() {
  const userInfo = useStore($userInfo)
  const config = useStore($config)
  const activityId = config?.activity_id

  return useQuery({
    queryKey: activityQueryKey(activityId),
    queryFn: () =>
      fetchActivityData(activityId!, userInfo!.user_id).catch(
        (error: unknown) => {
          throw tagFailure(error, 'load_activity')
        },
      ),
    select: (data) => {
      const { activity, itemTypes, claimableStepIds, claimableCreatedAtMap } =
        data
      const currentScore = activity.progress?.accumulated_score ?? 0

      const itemTypeMap = new Map<number, ItemType>(
        itemTypes.map((it) => [it.id, it]),
      )

      const checkpoints = [
        0,
        ...new Set(activity.progression_steps.map((s) => s.required_score)),
      ].sort((a, b) => a - b)

      function toReward(step: ActivityProgressionStep): Reward {
        const itemType = itemTypeMap.get(step.reward_item_type_id)
        const content = itemType?.item_contents[0]
        const status: RewardStatus = claimableStepIds.has(step.id)
          ? 'claimed'
          : step.required_score <= currentScore
            ? 'claimable'
            : 'pending'
        return {
          stepId: step.id,
          iconUrl: itemType?.icon_image_url ?? '',
          type: content?.type ?? 'coin',
          amount: content?.value ?? 0,
          titleKey: content
            ? contentTitleKey(content.type, content.value)
            : null,
          status,
          requiredScore: step.required_score,
          unlockCondition: step.unlock_condition,
          claimableCreatedAt: claimableCreatedAtMap.get(step.id) ?? null,
        }
      }

      const generalSteps = activity.progression_steps
        .filter((s) => s.unlock_condition === 'free')
        .sort((a, b) => a.required_score - b.required_score)

      const paidSteps = activity.progression_steps
        .filter((s) => s.unlock_condition !== 'free')
        .sort((a, b) => a.required_score - b.required_score)

      return {
        visibleRange: activity.visible_range,
        validRange: activity.valid_range,
        currentScore,
        checkpoints,
        generalTrackRewards: generalSteps.map(toReward),
        paidTrackRewards: paidSteps.map(toReward),
      }
    },
    enabled: !!userInfo && !!activityId,
    throwOnError: true,
  })
}
